import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface CardProps {
  children: ReactNode;
  /** Renders the filing tab above the card with this text. */
  tab?: string;
  tint?: 'white' | 'yellow' | 'pink' | 'blue';
  className?: string;
}

export function Card({ children, tab, tint = 'white', className = '' }: CardProps) {
  return (
    <section
      className={`card sheet-${tint} ${tab ? 'card-tabbed' : ''} ${className}`}
      {...(tab && { 'data-tab': tab })}
    >
      {children}
    </section>
  );
}

export function PageHeading({
  eyebrow,
  title,
  lead,
  actions,
}: {
  eyebrow?: string;
  title: string;
  lead?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div className="max-w-2xl">
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h1 className="text-3xl sm:text-4xl">{title}</h1>
        {lead && <p className="mt-3 text-[color:var(--color-ink-soft)]">{lead}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

/**
 * An empty state is an invitation to act, so it always names the next step
 * rather than only reporting that there is nothing here.
 */
export function Empty({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: { label: string; to?: string; onClick?: () => void };
}) {
  return (
    <div className="border border-dashed border-[color:var(--color-rule)] px-6 py-12 text-center">
      <p className="font-display text-lg font-semibold">{title}</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-[color:var(--color-ink-soft)]">{body}</p>
      {action && (
        <div className="mt-5">
          {action.to ? (
            <Link className="btn btn-secondary" to={action.to}>
              {action.label}
            </Link>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={action.onClick}>
              {action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Errors say what happened and what to do about it, in the interface's voice.
 * They do not apologise and they are never vague.
 */
export function Notice({
  tone = 'info',
  title,
  children,
  action,
}: {
  tone?: 'info' | 'warn' | 'error' | 'ok';
  title?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  const styles: Record<string, string> = {
    info: 'border-[color:var(--color-stamp)] bg-[color:var(--color-stamp-soft)]',
    ok: 'border-[color:var(--color-field)] bg-[color:var(--color-field-soft)]',
    warn: 'border-[color:var(--color-rule)] bg-[color:var(--color-copy-yellow)]',
    error: 'border-[color:var(--color-flag)] bg-[color:var(--color-flag-soft)]',
  };

  return (
    <div className={`border-l-4 px-4 py-3 ${styles[tone]}`} role={tone === 'error' ? 'alert' : undefined}>
      {title && <p className="font-semibold">{title}</p>}
      <div className="text-sm leading-relaxed">{children}</div>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 py-10 text-[color:var(--color-ink-soft)]" role="status">
      <span
        aria-hidden="true"
        className="h-4 w-4 animate-spin rounded-full border-2 border-[color:var(--color-rule)] border-t-[color:var(--color-stamp)]"
      />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/** A key/value row, laid out like a line on a record card. */
export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-[color:var(--color-rule)] py-2 last:border-b-0">
      <dt className="field-label mb-0">{label}</dt>
      <dd className="typed min-w-0 break-words text-right">{children}</dd>
    </div>
  );
}
